import {
    Add,
    Archive, // Archive icon for archiving projects
    KeyboardArrowDown,
    KeyboardArrowRight,
} from "@mui/icons-material";
import {
    Box,
    Button,
  Snackbar,
  Alert,
    Menu, // Add Menu import
    MenuItem, // Add MenuItem import
    Paper,
    Stack,
    styled,
    TextField,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import CreateEpicDialog from "../dialogs/EpicsDialog";
import CreateProjectDialog from "../dialogs/ProjectDialog";
import { useWorkspace } from "../../utils/WorkspaceContext";
import useDebounce from "../../hooks/debounceHook";
import AssigneeDropdown from "../../utils/AssigneeDropdown";


const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TaskRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0.3fr 182px 7fr 2fr 2fr 2fr 0fr",
  gap: theme.spacing(2),
  alignItems: "center",
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0fr 182px 7fr 2fr 2fr 2fr 0fr",
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: "4px",
  height: "100%",
  backgroundColor: color,
  borderRadius: "2px",
  marginRight: "8px",
}));

const GroupedProjects = () => {
  const [epics, setEpics] = useState([]);
  const [expandedEpics, setExpandedEpics] = useState({});
  const [openEpicDialog, setOpenEpicDialog] = useState(false);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const { currentWorkspace } = useWorkspace();

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Add new state for context menu
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Add context menu handlers
  const handleContextMenu = (event, project) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedProject(project);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedProject(null);
  };

  const getProjectArchiveErrorMessage = (error) => {
    const data = error?.response?.data;
    if (!data) return "Error archiving project.";

    if (typeof data === "string") return data;

    const detail = data.detail || data.error;
    if (detail && Array.isArray(data.unarchived_tracks)) {
      const trackNames = data.unarchived_tracks
        .map((track) => track?.name)
        .filter(Boolean);

      if (trackNames.length > 0) {
        const preview = trackNames.slice(0, 3).join(", ");
        const suffix =
          trackNames.length > 3
            ? ` (+${trackNames.length - 3} more)`
            : "";
        return `${detail} Unarchived tracks: ${preview}${suffix}`;
      }

      return detail;
    }

    if (detail) return detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const firstValue = data[firstKey];
        if (Array.isArray(firstValue) && firstValue[0]) {
          return String(firstValue[0]);
        }
      }
    }

    return "Error archiving project.";
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarState({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteProject = async () => {
    if (!selectedProject?.id) return;

    try {
      await api.delete(`projects/${selectedProject.id}/`, {
        params: { workspace_id: currentWorkspace.id },
      });
      // Refresh the projects list
      fetchEpics();
      showSnackbar('Project archived successfully.', 'success');
    } catch (error) {
      showSnackbar(getProjectArchiveErrorMessage(error), "error");
      console.error("Error deleting project:", error);
    }
    handleCloseContextMenu();
  };

  const debouncedUpdateProject = useDebounce(async (projectId, updates) => {
    try {
      await api.patch(`projects/${projectId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
      showSnackbar('Project updated successfully.', 'success');
      fetchEpics();
    } catch (error) {
      console.error("Error updating project:", error);
      showSnackbar('Error updating project.', 'error');
    }
  }, 1000);

  const fetchEpics = async () => {
    try {
      const response = await api.get("grouped-projects/", {params: {workspace_id: currentWorkspace.id}});
      setEpics(response.data.payload);
    } catch (error) {
      console.error("Error fetching epics:", error);
    }
  };

  useEffect(() => {
    fetchEpics();
  }, []);

  const toggleEpic = (epicId) => {
    setExpandedEpics((prev) => ({
      ...prev,
      [epicId]: !prev[epicId],
    }));
  };

  const handleCreateEpic = () => {
    setOpenEpicDialog(true);
  };

  const handleCreateProject = (epicId) => {
    setSelectedEpic(epicId);
    setOpenProjectDialog(true);
  };

  const handleProjectUpdate = (projectId, field, value) => {
    setEpics((prevEpics) =>
      prevEpics.map((epic) => ({
        ...epic,
        projects: epic.projects?.map((project) =>
          project.id === projectId 
            ? { ...project, [field]: value }
            : project
        )
      }))
    );
    
    debouncedUpdateProject(projectId, { [field]: value });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateEpic}
        >
          Create Epic
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateProject}
        >
          Create Project
        </Button>
      </Stack>

      <StyledPaper elevation={1}>
        {epics.map((epic) => (
          <Box key={epic.id}>
            <ProjectHeader onClick={() => toggleEpic(epic.epic_id)}>
              {expandedEpics[epic.epic_id] ? (
                <KeyboardArrowDown />
              ) : (
                <KeyboardArrowRight />
              )}
              <Typography variant="h6">{epic.epic_name}</Typography>
            </ProjectHeader>

            {expandedEpics[epic.epic_id] && (
              <>
                <HeaderRow>
                  <Box />
                  <Typography>Project Name</Typography>
                  <Typography>Description</Typography>
                  <Typography>Owner</Typography>
                  <Typography>Start Date</Typography>
                  <Typography>End Date</Typography>
                </HeaderRow>

                {epic.projects?.map((project) => (
                  <TaskRow 
                    key={project.id}
                    onContextMenu={(e) => handleContextMenu(e, project)} // Add context menu handler
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ColorBar color="#6B7280" />
                    </Box>

                    <TextField
                      fullWidth
                      variant="standard"
                      value={project.name}
                      onChange={(e) =>
                        handleProjectUpdate(project.id, "name", e.target.value)
                      }
                    />

                    <TextField
                      fullWidth
                      variant="standard"
                      value={project.description}
                      onChange={(e) =>
                        handleProjectUpdate(
                          project.id,
                          "description",
                          e.target.value
                        )
                      }
                    />

                    <AssigneeDropdown
                      currentSelected={project.project_manager}
                      onChange={(userId) =>
                        handleProjectUpdate(
                          project.id,
                          "project_manager",
                          userId
                        )
                      }
                    />

                    <TextField
                      variant="standard"
                      type="date"
                      value={project.start_date}
                      onChange={(e) =>
                        handleProjectUpdate(
                          project.id,
                          "start_date",
                          e.target.value
                        )
                      }
                    />

                    <TextField
                      variant="standard"
                      type="date"
                      value={project.end_date}
                      onChange={(e) =>
                        handleProjectUpdate(
                          project.id,
                          "end_date",
                          e.target.value
                        )
                      }
                    />
                  </TaskRow>
                ))}
              </>
            )}
          </Box>
        ))}
      </StyledPaper>

      {/* Add the context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={handleDeleteProject}
          sx={{
            color: "error.main",
            "&:hover": { bgcolor: "error.light" },
          }}
        >
          <Archive sx={{ mr: 1 }} fontSize="small" />
          Archive Project
        </MenuItem>
      </Menu>

      <CreateEpicDialog
        open={openEpicDialog}
        onClose={() => setOpenEpicDialog(false)}
        onSuccess={() => {
          setOpenEpicDialog(false);
          fetchEpics();
        }}
      />

      <CreateProjectDialog
        open={openProjectDialog}
        onClose={() => setOpenProjectDialog(false)}
        onSuccess={() => {
          setOpenProjectDialog(false);
          fetchEpics();
        }}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarState.severity}
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupedProjects;
